import React, { useEffect, useState } from "react";
import { CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import { API } from "../../../api";
import theme from "../../../theme";
import CustomInput from "../../../components/common/input/CustomInput";
import Loader from "../../../components/common/loader/Loader";
import {
  StyledInvoiceActionsRow,
  StyledInvoiceFieldRow,
  StyledInvoiceFieldRowTwo,
  StyledInvoiceFullWidthField,
  StyledInvoiceLoaderWrap,
  StyledInvoiceSaveButton,
  StyledInvoiceSectionCard,
  StyledInvoiceSectionTitle,
  StyledInvoiceSettingsDescription,
  StyledInvoiceSettingsHeader,
  StyledInvoiceSettingsPanel,
  StyledInvoiceSettingsTitle,
} from "./style";

const emptyForm = {
  bank_name: "",
  account_holder_name: "",
  account_number: "",
  ifsc_code: "",
  branch_name: "",
  notes: "",
  terms_and_conditions: "",
};

const inputColor = theme.palette?.custom?.productTextColor ?? "#1E293B";
const inputFontSize = theme.typography?.regular?.fontSize ?? "14px";

function InvoiceField({ label, placeholder, name, value, onChange, multiline, rows }) {
  return (
    <CustomInput
      formGrid
      isprofile={!multiline}
      color={inputColor}
      fontSize={inputFontSize}
      height={multiline ? "150px" : "10px"}
      customLabel={label}
      placeholder={placeholder || label}
      name={name}
      value={value}
      onChange={onChange}
      multiline={multiline}
      rows={rows}
      textAlign={
        multiline
          ? {
              textAlign: "left",
              "& .MuiInputBase-input": {
                textAlign: "left",
                verticalAlign: "top",
              },
            }
          : undefined
      }
    />
  );
}

export default function InvoiceSettingsPanel() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    API.getInvoiceSettings()
      .then((res) => {
        const body = res?.data?.data ?? res?.data ?? {};
        const settings = body?.settings || body || {};
        setForm({ ...emptyForm, ...settings });
      })
      .catch(() => toast.error("Could not load invoice settings"))
      .finally(() => setLoading(false));
  }, []);

  const updateField = (key) => (event) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.updateInvoiceSettings(form);
      const body = res?.data?.data ?? res?.data ?? {};
      const settings = body?.settings || body || {};
      setForm({ ...emptyForm, ...settings });
      toast.success("Invoice settings saved");
    } catch (err) {
      toast.error(err?.data?.message || "Could not save invoice settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <StyledInvoiceLoaderWrap>
        <Loader size={80} />
      </StyledInvoiceLoaderWrap>
    );
  }

  return (
    <StyledInvoiceSettingsPanel>
      <StyledInvoiceSettingsHeader>
        <StyledInvoiceSettingsTitle>Invoice settings</StyledInvoiceSettingsTitle>
        <StyledInvoiceSettingsDescription>
          Bank account details and terms &amp; conditions appear on generated invoice PDFs.
        </StyledInvoiceSettingsDescription>
      </StyledInvoiceSettingsHeader>

      <StyledInvoiceSectionCard>
        <StyledInvoiceSectionTitle>Bank account details</StyledInvoiceSectionTitle>

        <StyledInvoiceFieldRow>
          <InvoiceField
            label="Bank name"
            placeholder="HDFC Bank, Ram Nagar"
            name="bank_name"
            value={form.bank_name}
            onChange={updateField("bank_name")}
          />
          <InvoiceField
            label="Account holder name"
            placeholder="NRI2Native Solutions Pvt Ltd"
            name="account_holder_name"
            value={form.account_holder_name}
            onChange={updateField("account_holder_name")}
          />
          <InvoiceField
            label="Account number"
            placeholder="50200107982188"
            name="account_number"
            value={form.account_number}
            onChange={updateField("account_number")}
          />
        </StyledInvoiceFieldRow>

        <StyledInvoiceFieldRowTwo>
          <InvoiceField
            label="IFSC code"
            placeholder="HDFC0002407"
            name="ifsc_code"
            value={form.ifsc_code}
            onChange={updateField("ifsc_code")}
          />
          <InvoiceField
            label="Branch name"
            placeholder="Ram Nagar"
            name="branch_name"
            value={form.branch_name}
            onChange={updateField("branch_name")}
          />
        </StyledInvoiceFieldRowTwo>
      </StyledInvoiceSectionCard>

      <StyledInvoiceSectionCard>
        <StyledInvoiceSectionTitle>Notes &amp; terms</StyledInvoiceSectionTitle>

        <StyledInvoiceFullWidthField sx={{ mb: 2 }}>
          <InvoiceField
            label="Notes"
            placeholder="Thanks for your business."
            name="notes"
            value={form.notes}
            onChange={updateField("notes")}
          />
        </StyledInvoiceFullWidthField>

        <StyledInvoiceFullWidthField>
          <InvoiceField
            label="Terms & conditions"
            placeholder="All Cheques or Demand Drafts must be drawn in Favour of"
            name="terms_and_conditions"
            value={form.terms_and_conditions}
            onChange={updateField("terms_and_conditions")}
            multiline
            rows={6}
          />
        </StyledInvoiceFullWidthField>
      </StyledInvoiceSectionCard>

      <StyledInvoiceActionsRow>
        <StyledInvoiceSaveButton
          variant="contained"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : "Save settings"}
        </StyledInvoiceSaveButton>
      </StyledInvoiceActionsRow>
    </StyledInvoiceSettingsPanel>
  );
}
